import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Chrome,
  Download,
  Copy,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INSTALL_STEPS = [
  {
    icon: "⬇️",
    label: "Baixar .zip",
    desc: "Clique em Baixar .zip e salve o arquivo no computador",
    bg: "bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800",
    dot: "bg-blue-500",
  },
  {
    icon: "📂",
    label: "Descompactar",
    desc: "Extraia a pasta do arquivo .zip baixado",
    bg: "bg-violet-50 dark:bg-violet-950 border-violet-200 dark:border-violet-800",
    dot: "bg-violet-500",
  },
  {
    icon: "🔧",
    label: "chrome://extensions",
    desc: "Abra essa URL no Chrome e ative o Modo do desenvolvedor",
    bg: "bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800",
    dot: "bg-amber-500",
  },
  {
    icon: "📥",
    label: "Carregar sem compactacao",
    desc: "Clique em Carregar sem compactacao e selecione a pasta extraida",
    bg: "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800",
    dot: "bg-green-500",
  },
  {
    icon: "🔑",
    label: "Colar o token",
    desc: "Clique no icone da extensao no Chrome e cole o token gerado abaixo",
    bg: "bg-pink-50 dark:bg-pink-950 border-pink-200 dark:border-pink-800",
    dot: "bg-pink-500",
  },
];

function InstallGuide() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setActive((p) => (p + 1) % INSTALL_STEPS.length);
    }, 2500);
    return () => clearInterval(t);
  }, []);

  const step = INSTALL_STEPS[active];

  return (
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-sm p-4 mb-6">
      <p className="font-mono-label text-zinc-400 dark:text-zinc-500 mb-3">Como instalar a extensao</p>

      <div className="flex items-center gap-1.5 mb-3">
        {INSTALL_STEPS.map((s, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === active ? "w-6 " + s.dot : "w-1.5 bg-zinc-200"
            }`}
          />
        ))}
      </div>

      <div
        key={active}
        className={"flex items-center gap-4 border rounded-sm px-4 py-3 " + step.bg}
        style={{ animation: "fadeSlideIn 0.3s ease" }}
      >
        <span className="text-2xl flex-shrink-0">{step.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-mono-label text-zinc-500 dark:text-zinc-400" style={{ fontSize: "0.6rem" }}>
              PASSO {active + 1} DE {INSTALL_STEPS.length}
            </span>
          </div>
          <p className="text-sm font-semibold leading-tight">{step.label}</p>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{step.desc}</p>
        </div>
        <button
          onClick={() => setActive((p) => (p + 1) % INSTALL_STEPS.length)}
          className="h-7 w-7 rounded-full bg-white/70 dark:bg-zinc-800/70 hover:bg-white flex items-center justify-center flex-shrink-0 border border-zinc-200"
        >
          <ArrowRight className="h-3.5 w-3.5 text-zinc-500 dark:text-zinc-400" />
        </button>
      </div>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateX(8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

export default function ExtensionPage() {
  const { user, refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(user?.extension_token || "");

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/extension/connect");
      setToken(data.token);
      await refreshUser();
      toast.success("Token da extensao gerado");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Desconectar a extensao? Ela parara de postar ate voce gerar um novo token.")) return;
    setBusy(true);
    try {
      await api.post("/extension/disconnect");
      setToken("");
      await refreshUser();
      toast.info("Extensao desconectada");
    } finally {
      setBusy(false);
    }
  };

  const copyToken = async () => {
    await navigator.clipboard.writeText(token);
    toast.success("Token copiado");
  };

  return (
    <div className="p-6 md:p-8 max-w-[1000px]">
      <div className="mb-6">
        <p className="font-mono-label text-zinc-500 dark:text-zinc-400 mb-1">Extensao do navegador</p>
        <h1 className="font-display text-3xl font-bold tracking-tight">
          Postar de verdade nos grupos
        </h1>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-2 max-w-2xl">
          A Meta bloqueou a postagem automatica em grupos via API oficial em abril/2024. Nossa extensao
          contorna isso usando seu proprio Chrome ja autenticado: ela{" "}
          <strong>abre a pagina do grupo, preenche o texto, anexa a foto e publica</strong> exactamente
          como voce faria manualmente, mas automatizado e respeitando o delay configurado.
        </p>
      </div>

      <InstallGuide />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
          <div className="font-mono-label text-blue-700 dark:text-blue-400 mb-1">MODO AUTOMATICO</div>
          <p className="text-sm text-blue-900 dark:text-blue-100 leading-relaxed">
            Tudo e feito em segundo plano sem voce ver. Mais rapido. Bom para contas estabelecidas.
          </p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-sm p-4">
          <div className="font-mono-label text-purple-700 dark:text-purple-400 mb-1">MODO SEMI-AUTOMATICO</div>
          <p className="text-sm text-purple-900 dark:text-purple-100 leading-relaxed">
            A extensao abre o grupo, preenche tudo e{" "}
            <strong>espera voce clicar Publicar</strong>. Praticamente 100% de sucesso. Recomendado
            se sua conta for nova.
          </p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-6 flex gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900 dark:text-amber-100">
          <p className="font-medium mb-1">Boas praticas</p>
          <p className="leading-relaxed">
            Use delay maior ou igual a 60s entre posts. Nao envie mais de 50 posts/dia em uma conta nova.
            Postagens em massa muito agressivas podem fazer o Facebook bloquear temporariamente sua conta.
            Varie o conteudo com os botoes RANDOM T/D/M.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
        <Step
          n={1}
          title="Baixe a extensao"
          desc="Clique no botao e baixe o arquivo .zip"
          action={
            <a
              href={`${BACKEND_URL}/api/extension/download`}
              download="autopost-br-extension.zip"
              data-testid="ext-download-btn"
            >
              <Button className="rounded-sm bg-blue-600 hover:bg-blue-700 w-full">
                <Download className="h-4 w-4 mr-2" /> Baixar .zip
              </Button>
            </a>
          }
        />
        <Step
          n={2}
          title="Instale no Chrome"
          desc="Descompacte e ative o modo desenvolvedor em chrome://extensions"
          action={
            <a href="chrome://extensions" target="_blank" rel="noreferrer">
              <Button variant="outline" className="rounded-sm w-full">
                Abrir Extensoes <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </a>
          }
        />
        <Step
          n={3}
          title="Cole o token"
          desc="Gere abaixo, clique no icone da extensao e cole"
          action={null}
        />
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-sm p-6 mb-6">
        <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
          <Chrome className="h-5 w-5 text-blue-600" /> Token de conexao
        </h3>

        {token ? (
          <>
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm text-green-700 dark:text-green-400 font-medium">Extensao configurada</span>
            </div>

            <div>
              <label className="font-mono-label text-zinc-500 dark:text-zinc-400 mb-1 block">Token</label>
              <div className="flex gap-2">
                <Input
                  value={token}
                  readOnly
                  className="rounded-sm font-mono text-sm h-10"
                  data-testid="ext-token"
                />
                <Button onClick={copyToken} variant="outline" className="rounded-sm h-10">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-zinc-400 mt-1.5">
                Cole esse token no popup da extensao apos instala-la no Chrome.
              </p>
            </div>

            <div className="flex gap-2 mt-6">
              <Button
                onClick={generate}
                disabled={busy}
                variant="outline"
                className="rounded-sm"
                data-testid="ext-regenerate"
              >
                <RefreshCw className="h-4 w-4 mr-2" /> Gerar novo token
              </Button>
              <Button
                onClick={disconnect}
                disabled={busy}
                variant="outline"
                className="rounded-sm border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950"
                data-testid="ext-disconnect"
              >
                Desconectar
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center py-6">
            <Chrome className="h-12 w-12 text-zinc-300 dark:text-zinc-600 mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-4">
              Gere um token unico para autenticar sua extensao com o AutoPost BR.
            </p>
            <Button
              onClick={generate}
              disabled={busy}
              data-testid="ext-generate-btn"
              className="rounded-sm bg-blue-600 hover:bg-blue-700"
            >
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Gerar token de conexao
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-sm p-6">
        <h3 className="font-display text-lg font-semibold mb-3">Como funciona</h3>
        <ol className="space-y-2 text-sm text-zinc-700 dark:text-zinc-300 list-decimal list-inside">
          <li>Voce baixa a extensao, descompacta e carrega no Chrome (modo desenvolvedor).</li>
          <li>Gera o token aqui e cola no popup da extensao.</li>
          <li>Faz login no Facebook normalmente em outra aba (a extensao usa sua sessao).</li>
          <li>No painel, cria a campanha e clica em <strong>INICIAR DIVULGACAO</strong>.</li>
          <li>A cada 30 segundos, a extensao verifica a fila e processa <strong>1 grupo por vez</strong>.</li>
          <li>Para cada grupo: abre uma aba, cola o texto, anexa a foto e publica.</li>
          <li>Reporta sucesso/falha de volta para o painel em tempo real.</li>
          <li>Aguarda o delay configurado e processa o proximo grupo.</li>
        </ol>

        <h3 className="font-display text-lg font-semibold mt-6 mb-3">Limitacoes conhecidas</h3>
        <ul className="space-y-2 text-sm text-zinc-700 dark:text-zinc-300 list-disc list-inside">
          <li>O Facebook muda o DOM com frequencia. Se parar de postar, baixe a versao mais recente.</li>
          <li>Grupos privados sem permissao de postar serao marcados como falha.</li>
          <li>Posts com video ainda nao sao suportados — apenas texto e imagem.</li>
          <li>O Chrome precisa estar aberto. Se fechar todas as janelas, o worker para.</li>
        </ul>
      </div>
    </div>
  );
}

const Step = ({ n, title, desc, action }) => (
  <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-sm p-5">
    <div className="font-mono-label text-blue-600 mb-2">PASSO {n}</div>
    <h4 className="font-display font-semibold mb-1">{title}</h4>
    <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">{desc}</p>
    {action}
  </div>
);
